import NavigationBar from '../components/NavigationBar';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import { Sparkles, Target, AlertTriangle, Award, Star, PieChart, Scissors, Headphones, ThumbsUp, Handshake, Shield, Clock, Trophy } from 'lucide-react';

const AboutUsPage = ({ user }) => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
      <NavigationBar user={user} />
      
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 pb-4"
      >
        <div className="max-w-5xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6"
          >
            About Us
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gold font-light leading-relaxed"
          >
            We aren't just simplifying asset ownership; we're curating the ultimate lifestyle.
          </motion.p>
        </div>
      </motion.section>

      {/* The Story Section */}
      <section className="py-2 sm:py-3 md:py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 lg:p-12 border-2 hover:shadow-2xl transition-shadow duration-300" style={{ borderColor: '#D4AF37' }}>
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Star className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0" style={{ color: '#D4AF37' }} />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">The Story</h2>
            </div>
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
              <p>
                The Haute Circle was born from a simple yet powerful realisation by our founder, <span className="font-semibold text-black whitespace-nowrap">Mr. Aditya Tatuskar</span>. A third-generation entrepreneur deeply rooted in the world of luxury automobiles, Mr. Tatuskar led multiple authorised automobile dealerships in Karnataka under the Shakti Group umbrella.
              </p>
              <p>
                Over years of working closely with premium brands and discerning customers, he observed a recurring pattern: while people aspired to experience luxury cars and high-value lifestyle assets, traditional ownership often felt limiting, impractical, underutilised, and burdensome.
              </p>
              <p>
                With the nation growing stronger and our youth population seeking more aspirational lifestyles, we believe that there ought to be more options for truly living the elite lifestyle than just owning high-value assets.
              </p>
              <p>
                That's precisely what the Haute Circle aims to create: a complete lifestyle solution for the elite. This includes curating hassle-free luxury asset ownership, a lifestyle club with unique benefits, amenities, and concierge services, and creating opportunities for growth and networking through unique events and experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge Section */}
      <section className="py-2 sm:py-3 md:py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 border-2 hover:shadow-2xl transition-shadow duration-300" style={{ borderColor: '#D4AF37' }}>
            <div className="text-center mb-8 sm:mb-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12" style={{ color: '#D4AF37' }} />
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">The Challenge</h2>
              </div>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
                Traditional luxury ownership comes with barriers—even for high-net-worth individuals
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-gradient-to-br from-gold/5 to-gold/10 p-6 rounded-lg border-2 border-gold/30">
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ color: '#D4AF37' }}>High Acquisition Costs</h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Significant capital locked in depreciating assets</p>
              </div>
              <div className="bg-gradient-to-br from-gold/5 to-gold/10 p-6 rounded-lg border-2 border-gold/30">
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ color: '#D4AF37' }}>Maintenance Complexities</h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Insurance, servicing, documentation—constant hassles</p>
              </div>
              <div className="bg-gradient-to-br from-gold/5 to-gold/10 p-6 rounded-lg border-2 border-gold/30">
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ color: '#D4AF37' }}>Under-utilisation</h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Premium assets sitting idle, not delivering full value all the time</p>
              </div>
              <div className="bg-gradient-to-br from-gold/5 to-gold/10 p-6 rounded-lg border-2 border-gold/30">
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ color: '#D4AF37' }}>Compromised Resale</h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Rapid depreciation, undervaluation of unique features, unorganised secondary market and difficulties in finding the right buyer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-2 sm:py-3 md:py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 border-2 hover:shadow-2xl transition-shadow duration-300" style={{ borderColor: '#D4AF37' }}>
            <div className="flex items-center justify-center gap-3 mb-6">
              <Target className="w-10 h-10 sm:w-12 sm:h-12" style={{ color: '#D4AF37' }} />
              <h2 className="text-3xl md:text-4xl font-bold">The Solution</h2>
            </div>
            <div className="space-y-6 text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
              <p className="font-semibold text-black">
                This gap sparked the idea behind The Haute Circle: a curated platform that simplifies luxury, transforms ownership with flexibility, and elevates lifestyle.
              </p>
              <p>
                We exist to solve the complete ecosystem surrounding high-value assets by blending fractional ownership, new use-cases, concierge support, and a complete lifestyle enhancing approach.
              </p>
              <p>
                Our goal is to empower modern achievers to enjoy the freedom, flexibility, and prestige of luxury without the barriers that traditionally accompany it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-2 sm:py-3 md:py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 border-2 hover:shadow-2xl transition-shadow duration-300" style={{ borderColor: '#D4AF37' }}>
            <div className="flex items-center justify-center gap-3 mb-8 sm:mb-12">
              <Award className="w-10 h-10 sm:w-12 sm:h-12" style={{ color: '#D4AF37' }} />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center">What We Offer</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <PieChart className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Fractional Ownership</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Access luxury assets without full capital commitment while enabling flexibility</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <Scissors className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Bespoke Solutions</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Unique offering for unique needs</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <Headphones className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Concierge Support</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">White-glove service, end-to-end</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <ThumbsUp className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Experience-First</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Focus on enjoyment, we handle the rest</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise Section */}
      <section className="py-2 sm:py-3 md:py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 border-2 hover:shadow-2xl transition-shadow duration-300" style={{ borderColor: '#D4AF37' }}>
            <div className="flex items-center justify-center gap-3 mb-8 sm:mb-12">
              <Trophy className="w-10 h-10 sm:w-12 sm:h-12" style={{ color: '#D4AF37' }} />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center">Our Promise</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <Handshake className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Trust with Transparency</h3>
                  <p className="text-sm sm:text-base md:text-lg font-semibold mb-2" style={{ color: '#D4AF37' }}>"Clear Commitments, Honest Dealings"</p>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">We prioritise openness in every interaction, ensuring that your luxury asset journey is built on a foundation of integrity and clarity.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <ThumbsUp className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Ease of Use</h3>
                  <p className="text-sm sm:text-base md:text-lg font-semibold mb-2" style={{ color: '#D4AF37' }}>"Luxury Simplified, Experiences Amplified"</p>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Navigating high-value assets has never been simpler. We streamline every process so you can focus on enjoyment without the operational hassles.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">End-to-End Support</h3>
                  <p className="text-sm sm:text-base md:text-lg font-semibold mb-2" style={{ color: '#D4AF37' }}>"Comprehensive Care, Every Step of the Way"</p>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">From acquisition to management, we're with you at every stage, offering tailored solutions that cover all aspects of your luxury lifestyle.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#D4AF37' }}>
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4AF37' }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2">Consistent Support at All Times</h3>
                  <p className="text-sm sm:text-base md:text-lg font-semibold mb-2" style={{ color: '#D4AF37' }}>"Always There, Always Reliable"</p>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">Our commitment extends beyond business hours — providing unwavering support whenever you need it, ensuring peace of mind at every turn.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">Ready to Join The Circle?</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8">
            Experience luxury the way it should be—effortless, flexible, and unforgettable.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a 
              href="/register-interest" 
              className="px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all inline-block text-black hover:opacity-90"
              style={{ backgroundColor: '#D4AF37' }}
            >
              Express Interest
            </a>
            <a 
              href="/high-assets" 
              className="bg-white hover:bg-gray-100 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all inline-block"
            >
              Explore High Assets
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutUsPage;
